import { JointInfo, WallConnectionInfo } from './JointDetector';
import type { FloorplannerStore } from '../store/floorplannerStore';
import { sharedJointStateManager } from './SharedJointStateManager';

// Lazy accessor to avoid circular dependency with floorplannerStore
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - JointCoordinator called too early');
    }
  }
  return _floorplannerStore;
};

/**
 * JointCoordinator manages the processing order of walls at multi-wall joints.
 *
 * This class solves the T-connection wall trimming problem by ensuring walls
 * are processed in a consistent, priority-based order. It prevents order-dependent
 * results by coordinating which wall should be processed first in complex joints.
 *
 * The priority system:
 * 1. Widest walls first (they define joint geometry)
 * 2. End connections before middle connections (T-joint stems process last)
 * 3. Wall ID alphabetically (deterministic tiebreaker)
 */
export class JointCoordinator {
  private dirtyJoints: Set<string>;
  private processedWallsInJoint: Map<string, Set<string>>;
  private jointProcessingOrder: Map<string, string[]>;

  constructor() {
    this.dirtyJoints = new Set();
    this.processedWallsInJoint = new Map();
    this.jointProcessingOrder = new Map();
  }

  /**
   * Sort the connected walls in a joint by priority.
   * Returns a new sorted array without mutating the original.
   */
  sortWallsByPriority(joint: JointInfo): WallConnectionInfo[] {
    return [...joint.connectedWalls].sort((a, b) => {
      // 1. Compare widths (descending - larger widths first)
      const widthA = a.wall.wallWidth || getFloorplannerStore().wallWidth;
      const widthB = b.wall.wallWidth || getFloorplannerStore().wallWidth;
      if (widthA !== widthB) return widthB - widthA; // Descending

      // 2. Compare connection type priority (ascending - lower priority first)
      const priorityA = this.getConnectionTypePriority(a.connectionPosition);
      const priorityB = this.getConnectionTypePriority(b.connectionPosition);
      if (priorityA !== priorityB) return priorityA - priorityB; // Ascending

      // 3. Compare wall IDs (ascending - alphabetical)
      return a.wallId.localeCompare(b.wallId);
    });
  }

  /**
   * Get the processing order for walls in a joint.
   * Caches the result to avoid repeated sorting.
   */
  getProcessingOrder(joint: JointInfo): string[] {
    // Check if we have a cached order
    if (this.jointProcessingOrder.has(joint.id)) {
      return [...this.jointProcessingOrder.get(joint.id)!];
    }

    // Generate and cache the order
    const sortedWalls = this.sortWallsByPriority(joint);
    const wallIds = sortedWalls.map(w => w.wallId);
    this.jointProcessingOrder.set(joint.id, [...wallIds]);

    return [...wallIds];
  }

  /**
   * Get the primary (highest priority) wall in a joint.
   * Returns null if no walls exist.
   * Uses persistent state for multi-wall joints when available.
   */
  getPrimaryWall(joint: JointInfo): WallConnectionInfo | null {
    // For multi-wall joints with persistent state, use the persistent primary
    if (joint.persistentState && joint.connectedWalls.length >= 3) {
      const persistentPrimaryId = joint.persistentState.primaryWallId;
      const persistentPrimary = joint.connectedWalls.find(w => w.wallId === persistentPrimaryId);

      if (persistentPrimary) {
        return persistentPrimary;
      }

      // If persistent primary is not found in current connections, fall back to sorting
      console.warn('JointCoordinator: Persistent primary wall not found in current connections', {
        jointId: joint.id,
        persistentPrimaryId,
        currentWalls: joint.connectedWalls.map(w => w.wallId)
      });
    }

    // Fallback to sorting-based priority
    const sortedWalls = this.sortWallsByPriority(joint);
    return sortedWalls.length > 0 ? sortedWalls[0] : null;
  }

  /**
   * Mark a joint as dirty, clearing its processed state and cached order.
   */
  markJointDirty(jointId: string): void {
    this.dirtyJoints.add(jointId);
    this.processedWallsInJoint.delete(jointId);
    this.jointProcessingOrder.delete(jointId);
  }

  /**
   * Mark a joint as clean (processing complete).
   */
  markJointClean(jointId: string): void {
    this.dirtyJoints.delete(jointId);
  }

  /**
   * Check if a joint is marked as dirty (needs processing).
   */
  isJointDirty(jointId: string): boolean {
    return this.dirtyJoints.has(jointId);
  }

  /**
   * Mark a specific wall as processed in a joint.
   */
  markWallProcessed(jointId: string, wallId: string): void {
    if (!this.processedWallsInJoint.has(jointId)) {
      this.processedWallsInJoint.set(jointId, new Set());
    }
    this.processedWallsInJoint.get(jointId)!.add(wallId);
  }

  /**
   * Check if a specific wall has been processed in a joint.
   */
  isWallProcessed(jointId: string, wallId: string): boolean {
    const processedWalls = this.processedWallsInJoint.get(jointId);
    return processedWalls ? processedWalls.has(wallId) : false;
  }

  /**
   * Get the set of processed walls for a joint.
   */
  getProcessedWalls(jointId: string): Set<string> {
    return this.processedWallsInJoint.get(jointId) || new Set();
  }

  /**
   * Clear all data for a specific joint.
   */
  clearJoint(jointId: string): void {
    this.dirtyJoints.delete(jointId);
    this.processedWallsInJoint.delete(jointId);
    this.jointProcessingOrder.delete(jointId);
  }

  /**
   * Clear all tracked data.
   */
  clearAll(): void {
    this.dirtyJoints.clear();
    this.processedWallsInJoint.clear();
    this.jointProcessingOrder.clear();
  }

  /**
   * Get statistics about the current state.
   */
  getStats(): {
    dirtyJointsCount: number;
    totalJointsTracked: number;
    totalProcessedWalls: number;
    cachedProcessingOrders: number;
  } {
    let totalProcessedWalls = 0;
    for (const processedWalls of this.processedWallsInJoint.values()) {
      totalProcessedWalls += processedWalls.size;
    }

    return {
      dirtyJointsCount: this.dirtyJoints.size,
      totalJointsTracked: this.processedWallsInJoint.size,
      totalProcessedWalls,
      cachedProcessingOrders: this.jointProcessingOrder.size
    };
  }

  /**
   * Get priority value for connection position.
   * Lower values are processed first.
   */
  private getConnectionTypePriority(connectionPosition: 'start' | 'end' | 'middle'): number {
    // End/start connections (T-joint top) process before middle connections (T-joint stem)
    return connectionPosition === 'middle' ? 1 : 0;
  }
}

// Export singleton instance following the pattern used by JointDetector
export const jointCoordinator = new JointCoordinator();